/**
 * `UserManager` class
 *
 * Represents a user manager that manages user data fetched from various sources.
 *
 * This class allows the client to fetch and manage user details from the provided sources.
 * It fetches user details such as name, ID, and various other IDs related to the user.
 */
import gsap from 'gsap';
import { jwtDecode } from 'jwt-decode';

import { FetchData } from '../cloneme-classes/FetchData';
import { getCurrentDomainFolder } from '../utils';

declare const AUTH0_DOMAIN: string;
declare const CMS_URL: string;

interface IUserManager {
  init(): Promise<void>;
  getUserName(): string;
  getJWT(): string;
  getAvatarID(): number;
  getInfluencerID(): number; // IDENTITY ID
  getDigitalCloneID(): number;
  setAvatarID(_id: number): void;
  getVoiceCloneELID(): string;
  getVoiceCloneID(): number;
  setVoiceCloneID(_id: number): void;
  setVoiceCloneELID(_id: string): void;
  getCreatorProfileState(): number;
  getTwin(): TwinDataModel;
  get userName(): string;
  get avatarID(): number;
  get digitalCloneID(): number;
  get voiceCloneID(): number;
  get voiceCloneELID(): string;
  get digitalTwin(): TwinDataModel;
  get influencerID(): number;
  get wallet(): WalletDataModel;
  get userID(): number;
  get hasActiveSubscription(): boolean;
  get email(): string;
  get status(): number;
  get firstName(): string;
  get lastName(): string;
  get phone(): string;
  get canChangePassword(): boolean;
  sendPasswordUpdateEmail(): void;
}

const BASE_URL = CMS_URL;

type TwinDataModel = {
  name: string;
  specialityLabel: string;
  introParagraph: string;
  chatVideoIntroScript: string;
  interactionStyle: string;
};

type SubscriptionModel = {
  name: string;
  validUntil: string;
  id: string;
  product: string;
};

type TokensInfoModel = {
  number: number;
  validUntil: string;
};

type KeysInfoModel = {
  number: number;
  validUntil: string;
};

type WalletDataModel = {
  tokens: number;
  keys: number;
  subscription: SubscriptionModel | undefined;
  tokensInfo: TokensInfoModel | undefined;
  keysInfo: KeysInfoModel | undefined;
};

type Plan = {
  id: number;
  uuid: string | null;
  price: number;
};

type SubscriptionPlan = {
  id: number;
  premium_creator: Plan;
  premium: Plan;
  essentials: Plan;
  starter: Plan;
};

type SubscriptionData = {
  id: number;
  yearly: SubscriptionPlan;
  biannually: SubscriptionPlan;
  monthly: SubscriptionPlan;
};

type AuthInfo = {
  provider: string;
  clientId: string;
  expirationDate: Date;
};

export class UserManager implements IUserManager {
  public static PROFILE_STATE__NEW = 0;
  public static PROFILE_STATE__AVATAR_SETUP = 1;
  public static PROFILE_STATE__VOICE_SETUP = 2;
  public static PROFILE_STATE__TWIN_TRAINED = 3;
  public static PROFILE_STATE__PROFILE_REVIEWED = 4;
  public static PROFILE_STATE__COMPLETED_DRAFT = 5;
  public static PROFILE_STATE__PUBLISHED = 6;

  public static AUTH_STATUS__LOGGED_OUT = 0;
  public static AUTH_STATUS__LOGGED_IN = 1;

  private _userID = 0;
  private _userName = '';
  private _email = '';
  private _firstName = '';
  private _lastName = '';
  private _phone = '';
  private userNameStrapi = '';
  private _avatarID = 0;
  private _influencerID = 0;
  private _digitalCloneID = 0;
  private _voiceCloneID = 0;
  private _voiceCloneELID = '';
  private _twin: TwinDataModel = {
    name: '',
    specialityLabel: '',
    introParagraph: '',
    chatVideoIntroScript: '',
    interactionStyle: '',
  };

  private _wallet: WalletDataModel = {
    tokens: 0,
    keys: 0,
    subscription: undefined,
    tokensInfo: undefined,
    keysInfo: undefined,
  };

  // this data will be filled with the current subscription plan of the user
  private currentCreatorProfileState: number = UserManager.PROFILE_STATE__NEW;
  private _loginStatus: number = UserManager.AUTH_STATUS__LOGGED_OUT;
  private hasInitialized = false;
  private authInfo: AuthInfo = {
    provider: null,
    clientId: null,
    expirationDate: null,
  };

  private static instance: UserManager;

  // private constructor() {}

  public static async getInstance(): Promise<UserManager> {
    if (!UserManager.instance) {
      UserManager.instance = new UserManager();
      await UserManager.instance.init();
    }

    return UserManager.instance;
  }

  public async init(): Promise<void> {
    try {
      if (this.hasInitialized) return;
      this.hasInitialized = true;
      this.initialize.bind(this)();
      if (this._loginStatus === UserManager.AUTH_STATUS__LOGGED_OUT) {
        this.setButtonsState.bind(this)();
        this.setElementState();
        // throw new Error('You are not logged in.');
        return;
      }

      const user = await FetchData.fetchUser();

      if (!user) {
        this._loginStatus = UserManager.AUTH_STATUS__LOGGED_OUT;
        this.setButtonsState.bind(this)();
        this.setElementState();
        return;
      }

      const name = user.username || user.alias;
      this.setButtonsState.bind(this)();

      this._userName = name;
      this._email = user.email ?? '';
      this._firstName = user.firstName ?? '';
      this._lastName = user.lastName ?? '';
      this._phone = user?.info?.mobileNumber ?? '';
      this._userID = user.id;

      const digitalTwin = user?.digitalTwin;

      if (user) {
        this.userNameStrapi = user.username;
        const wallet = await FetchData.fetchUserWallet();
        if (wallet) {
          this._wallet.tokens = wallet?.attributes?.tokens;
          this._wallet.keys = wallet?.attributes?.keys;
          this._wallet.subscription = wallet?.attributes?.subscription;
          this._wallet.keysInfo = wallet?.attributes?.keysInfo;
          this._wallet.tokensInfo = wallet?.attributes?.tokensInfo;
        }
      }

      if (digitalTwin) {
        this._voiceCloneID = digitalTwin.voiceClone?.id;
        this._voiceCloneELID = digitalTwin.voiceClone?.elevenLabsId;
        if (!this._voiceCloneID) {
          this.currentCreatorProfileState = UserManager.PROFILE_STATE__AVATAR_SETUP;
        } else {
          this.currentCreatorProfileState = UserManager.PROFILE_STATE__VOICE_SETUP;
        }

        this._avatarID = digitalTwin.avatar?.id;
        this._twin.name = digitalTwin.name;
        this._twin.specialityLabel = digitalTwin.specialityLabel;
        this._twin.introParagraph = digitalTwin?.identity?.introParagraph || '';
        this._twin.chatVideoIntroScript = digitalTwin?.identity?.chatVideoIntroScript || '';
        this._twin.interactionStyle = digitalTwin.interactionStyle;

        if (!this._avatarID) {
          this.currentCreatorProfileState = UserManager.PROFILE_STATE__NEW;
        }

        this._influencerID = digitalTwin.identity?.id;
        this._digitalCloneID = digitalTwin.id;
        if (this._influencerID) {
          this.currentCreatorProfileState = UserManager.PROFILE_STATE__TWIN_TRAINED;
        }
      }
    } catch (error) {
      console.error('Error while initializing user:', error);
    }
    this.logUserDetails();
    this.setupCurrentStatus();
    this.setUserFields();
    this.setElementState();
    this.setBillingButtonsVisibility();
  }

  private setBillingButtonsVisibility(): void {
    if (this.hasActiveSubscription) return;
    const billingBtns = document.querySelectorAll<HTMLElement>(
      '[data-backend-action="billing-dashboard"]'
    );
    billingBtns.forEach((el) => {
      el.style.display = 'none';
    });
  }

  private setupCurrentStatus() {
    // Select all elements with a `data-profile-step` attribute
    const allStepElements = document.querySelectorAll('[data-profile-step]');
    gsap.from(allStepElements, { duration: 0.2, autoAlpha: 0, y: 0, stagger: 0.1 });
    //console.log('Current state:', this.currentCreatorProfileState);
    allStepElements.forEach((element) => {
      const stepValue = parseInt(element.getAttribute('data-profile-step') ?? '0') || 0;

      // Check if the element's step value is greater than the current state
      if (stepValue > this.currentCreatorProfileState) {
        element.classList.add('disabled');
        element.classList.remove('enabled');
        element.removeAttribute('href');
      } else {
        // Optionally ensure elements of the current step or below are enabled
        element.classList.add('enabled');
        element.classList.remove('disabled');
        //   element.classList.remove('current');
      }
      // if (stepValue === this.currentCreatorProfileState) {
      //   //  element.classList.add('current');
      //   gsap.from(element, { duration: 0.3, scale: 1.2 });
      // }
    });
  }

  private setUserFields() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    function applyValuesToElement({ el, sourceObject, elementPropertyTarget }) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (sourceObject[elementPropertyTarget]) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (el instanceof HTMLInputElement) {
          el.value = sourceObject[elementPropertyTarget];
        } else {
          el.textContent = sourceObject[elementPropertyTarget];
        }
      }
    }

    const statusUserActionableItems = document.querySelectorAll('[data-profile-value]');
    statusUserActionableItems.forEach((el) => {
      const elementPropertyTarget = el.getAttribute('data-profile-value') || '';
      applyValuesToElement({ el, sourceObject: this, elementPropertyTarget });
    }, this);

    const statusTwinActionableItems = document.querySelectorAll('[data-twin-value]');
    statusTwinActionableItems.forEach((el) => {
      const elementPropertyTarget = el.getAttribute('data-twin-value') || '';

      applyValuesToElement({ el, sourceObject: this._twin, elementPropertyTarget });
    }, this);
  }

  getJWT(): string {
    return localStorage.getItem('jwt');
  }

  getUserName(): string {
    return this._userName;
  }

  get userName(): string {
    return this._userName;
  }

  get firstName(): string {
    return this._firstName;
  }

  get lastName(): string {
    return this._lastName;
  }

  get phone(): string {
    return this._phone;
  }

  get email(): string {
    return this._email;
  }

  get avatarID(): number {
    return this._avatarID;
  }

  getAvatarID(): number {
    return this._avatarID;
  }

  getInfluencerID(): number {
    return this._influencerID;
  }

  get influencerID(): number {
    return this._influencerID;
  }

  getDigitalCloneID(): number {
    return this._digitalCloneID;
  }

  get digitalCloneID(): number {
    return this._digitalCloneID;
  }

  getVoiceCloneID(): number {
    return this._voiceCloneID;
  }

  get voiceCloneID(): number {
    return this._voiceCloneID;
  }

  getVoiceCloneELID(): string {
    return this._voiceCloneELID;
  }

  get voiceCloneELID(): string {
    return this._voiceCloneELID;
  }

  getCreatorProfileState(): number {
    return this.currentCreatorProfileState;
  }

  setAvatarID(_id: number) {
    this._avatarID = _id;
  }

  setVoiceCloneID(_id: number) {
    this._voiceCloneID = _id;
  }

  setVoiceCloneELID(_id: string) {
    this._voiceCloneELID = _id;
  }

  getTwin(): TwinDataModel {
    return this._twin;
  }

  get digitalTwin(): TwinDataModel {
    return this._twin;
  }

  get wallet(): WalletDataModel {
    return this._wallet;
  }

  get userID(): number {
    return this._userID;
  }

  get hasActiveSubscription(): boolean {
    return this?._wallet?.subscription != null && this?._wallet?.subscription !== undefined;
  }

  get status(): number {
    return this._loginStatus;
  }

  get canChangePassword(): boolean {
    return this.authInfo?.provider?.includes('google');
  }

  public sendPasswordUpdateEmail(): void {
    if (this.canChangePassword) {
      FetchData.sendPasswordUpdateEmail({
        email: this.email,
        clientId: this.authInfo.clientId,
      }).then((data: string): void => {
        console.log(data);
        //TODO: Do something with this.
      });
    }
  }

  private logUserDetails() {
    const userDetails = {
      UserName: this._userName,
      //'Avatar ID': this._avatarID,
      //'Digital Clone ID': this._digitalCloneID,
      //'influencer ID': this._influencerID,
      //'voiceClone ID': this._voiceCloneID,
      //'voiceCloneEL ID': this._voiceCloneELID,
    };

    for (const [label, value] of Object.entries(userDetails)) {
      console.log(`👤 ${label}: ${value}`);
    }
  }

  private setLoginStatus(): void {
    let isLoggedIn = !!localStorage.getItem('jwt');
    if (!this.authInfo.clientId /* || this.authInfo.expirationDate < new Date()*/) {
      isLoggedIn = false;
    }
    this._loginStatus = isLoggedIn
        ? UserManager.AUTH_STATUS__LOGGED_IN
        : UserManager.AUTH_STATUS__LOGGED_OUT;

    console.log('userManager.status Setting login status:', this._loginStatus);
  }

  private setElementState(): void {
    console.log('Setting element state');
    const elementsLoggedOut: NodeListOf<HTMLElement> =
        document.querySelectorAll('[cc-state="logged-out"]');
    const elementsLoggedIn: NodeListOf<HTMLElement> =
        document.querySelectorAll('[cc-state="logged-in"]');

    if (this._loginStatus) {
      console.log('Logged in');
      elementsLoggedOut.forEach((element: HTMLElement) => {
        element.classList.add('hide');
      });
      elementsLoggedIn.forEach((element: HTMLElement) => {
        element.classList.remove('hide');
      });
    } else {
      elementsLoggedIn.forEach((element: HTMLElement) => {
        element.classList.add('hide');
      });
      elementsLoggedOut.forEach((element: HTMLElement) => {
        element.classList.remove('hide');
      });
    }
  }

  private setButtonsState(): void {
    const loginBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="login"]'
    );
    const registerBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="register"]'
    );
    const logoutBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="logout"]'
    );

    if (this._loginStatus) {
      logoutBtns.forEach((button: HTMLAnchorElement) => {
        button.style.display = 'block';
      });
      loginBtns.forEach((button: HTMLAnchorElement) => {
        button.style.display = 'none';
      });
      registerBtns.forEach((button: HTMLAnchorElement) => {
        button.style.display = 'none';
      });
    } else {
      logoutBtns.forEach((button: HTMLAnchorElement) => {
        button.style.display = 'none';
      });
      loginBtns.forEach((button: HTMLAnchorElement) => {
        button.style.display = 'block';
      });
      registerBtns.forEach((button: HTMLAnchorElement) => {
        button.style.display = 'block';
      });
    }
  }

  private logout(e: Event): void {
    e.preventDefault();
    const auth0JWT: string | null = localStorage.getItem('auth0_jwt');
    const redirectUrl = `${getCurrentDomainFolder()}/auth/logout-redirect`;

    this.setLoginStatus();
    this.setButtonsState();

    const logoutUrl = `${AUTH0_DOMAIN}/oidc/logout?id_token_hint=${auth0JWT}&post_logout_redirect_uri=${redirectUrl}`;
    window.location.href = logoutUrl;
  }

  private login(e: Event): void {
    e.preventDefault();
    localStorage.setItem('redirect-url', encodeURIComponent(window.location.href));
    const redirectUrl = `${getCurrentDomainFolder()}/auth/login-redirect`;

    window.location.href = `${BASE_URL}api/connect/auth0?callback=${redirectUrl}&screen_hint=signup`;
  }

  private loginUser(e: Event): void {
    e.preventDefault();
    localStorage.setItem('redirect-url', encodeURIComponent(window.location.href));
    const redirectUrl = `${getCurrentDomainFolder()}/auth/login-redirect`;

    window.location.href = `${BASE_URL}api/connect/auth0?callback=${redirectUrl}`;
  }

  private register(e: Event): void {
    e.preventDefault();
    localStorage.setItem('redirect-url', encodeURIComponent(window.location.href));
    const redirectUrl = `${getCurrentDomainFolder()}/auth/login-redirect`;

    window.location.href = `${BASE_URL}api/connect/auth0?callback=${redirectUrl}&screen_hint=signup`;
  }

  private showBillingDashboard(e: Event): void {
    e.preventDefault();
    if (!this.hasActiveSubscription) {
      window.location.href = `${getCurrentDomainFolder()}/payments/pricing-ecsuite`;
      return;
    }
    openCancelSubscriptionModal();
  }

  private initializeLogoutButtons(): void {
    const logoutBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="logout"]'
    );
    logoutBtns?.forEach((button: HTMLAnchorElement) => {
      button.addEventListener('click', this.logout.bind(this));
    });
  }

  private initializeLoginButtons(): void {
    const loginBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="login"]'
    );
    loginBtns?.forEach((button: HTMLAnchorElement) => {
      button.addEventListener('click', this.login);
    });
  }

  private initializeForceLoginButtons(): void {
    const loginBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="login-user"]'
    );
    loginBtns?.forEach((button: HTMLAnchorElement) => {
      button.addEventListener('click', this.loginUser);
    });
  }

  private initializeRegisterButtons(): void {
    const registerBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="register"]'
    );
    registerBtns?.forEach((button: HTMLAnchorElement) => {
      button.addEventListener('click', this.register);
    });
  }

  private initializeBillingButtons(): void {
    const billingBtns: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(
        'a[data-backend-action="billing-dashboard"]'
    );
    billingBtns?.forEach((button: HTMLAnchorElement) => {
      button.addEventListener('click', this.showBillingDashboard.bind(this));
    });
  }

  private setupAuthInfo(token: string | null): void {
    // Decode the token without verifying the signature
    if (!token) return;

    const decoded = jwtDecode(token);
    if (!decoded) {
      throw new Error('Invalid token');
    }

    // Extract the client_id or aud (audience)
    this.authInfo.clientId = decoded.aud + '';
    const date = new Date();
    date.setUTCSeconds(decoded.exp);
    this.authInfo.expirationDate = date;
    this.authInfo.provider = decoded.sub.split('|')[0];
    if (!this.authInfo.clientId) {
      throw new Error('Client ID not found in token');
    }
  }

  private initialize(): void {
    this.initializeLogoutButtons.bind(this)();
    this.initializeLoginButtons.bind(this)();
    this.initializeForceLoginButtons.bind(this)();
    this.initializeRegisterButtons.bind(this)();
    this.initializeBillingButtons.bind(this)();
    // this.setButtonsState.bind(this)();
    this.setupAuthInfo(localStorage.getItem('auth0_jwt'));
    this.setLoginStatus.bind(this)();
    FetchData.setJWTToken(localStorage.getItem('jwt'));
  }
}

const MODAL_ID = 'cc-cancel-sub-modal';

function buildModalHTML(): string {
  return `
    <div data-role="backdrop" style="position:fixed;inset:0;background:rgba(0,0,0,0.7);backdrop-filter:blur(4px);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;font-family:Inter,system-ui,sans-serif;">
      <div data-role="card" style="background:#0a0a0a;border:1px solid rgba(255,255,255,0.1);border-radius:16px;max-width:400px;width:100%;padding:32px;text-align:center;position:relative;color:#fff;">
        <button data-role="close" aria-label="Close" style="position:absolute;top:12px;right:16px;background:transparent;border:0;color:rgba(255,255,255,0.4);font-size:24px;cursor:pointer;line-height:1;">×</button>
        <h3 data-role="title" style="font-size:20px;font-weight:600;margin:0 0 16px;">Cancel Subscription</h3>
        <p data-role="body" style="color:rgba(255,255,255,0.6);margin:0 0 24px;line-height:1.5;">Are you sure? Your remaining tokens will stay active until they expire.</p>
        <div data-role="actions" style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap;">
          <button data-role="keep" style="padding:10px 24px;border-radius:9999px;font-size:14px;font-weight:600;border:1px solid rgba(255,255,255,0.3);color:#fff;background:transparent;cursor:pointer;">Keep Plan</button>
          <button data-role="confirm" style="padding:10px 24px;border-radius:9999px;font-size:14px;font-weight:600;border:0;color:#fff;background:#dc2626;cursor:pointer;">Yes, Cancel</button>
        </div>
      </div>
    </div>
  `;
}

function openCancelSubscriptionModal(): void {
  if (document.getElementById(MODAL_ID)) return;

  const wrap = document.createElement('div');
  wrap.id = MODAL_ID;
  wrap.innerHTML = buildModalHTML();
  document.body.appendChild(wrap);

  const close = () => wrap.remove();
  const $ = (sel: string) => wrap.querySelector(sel) as HTMLElement;

  $('[data-role="backdrop"]').addEventListener('click', (ev) => {
    if (ev.target === ev.currentTarget) close();
  });
  $('[data-role="close"]').addEventListener('click', close);
  $('[data-role="keep"]').addEventListener('click', close);

  const confirmBtn = $('[data-role="confirm"]') as HTMLButtonElement;
  const titleEl = $('[data-role="title"]');
  const bodyEl = $('[data-role="body"]');
  const actionsEl = $('[data-role="actions"]');

  const showResult = (msg: string, reload: boolean) => {
    bodyEl.textContent = msg;
    actionsEl.innerHTML = `<button data-role="ok" style="padding:10px 24px;border-radius:9999px;font-size:14px;font-weight:600;border:1px solid rgba(255,255,255,0.3);color:#fff;background:transparent;cursor:pointer;">Close</button>`;
    actionsEl.querySelector('[data-role="ok"]')!.addEventListener('click', () => {
      close();
      if (reload) window.location.reload();
    });
  };

  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Canceling...';
    confirmBtn.style.opacity = '0.5';
    try {
      const res = await FetchData.cancelSubscription();
      if (res?.success) {
        titleEl.textContent = 'Subscription Canceled';
        showResult('Your subscription has been canceled. Remaining tokens stay active until they expire.', true);
      } else {
        showResult(res?.error || 'Unable to cancel subscription. Please contact support.', false);
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      showResult('Something went wrong. Please contact support.', false);
    }
  });
}
